import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SessionUser } from '@tb/shared';
import { api } from '../api/endpoints';
import { RequestError, tokenStore } from '../api/client';
import { getInitData, isInsideTelegram } from '../telegram/telegram';

type SessionState =
  | { status: 'loading' }
  | { status: 'ready'; user: SessionUser }
  | { status: 'outside' }
  | { status: 'error'; message: string };

const SessionContext = createContext<SessionState>({ status: 'loading' });

export const useSession = (): SessionState => useContext(SessionContext);

export function useSessionUser(): SessionUser {
  const session = useSession();
  if (session.status !== 'ready') {
    throw new Error('useSessionUser called outside an authenticated screen');
  }
  return session.user;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const initData = getInitData();

      if (!initData) {
        if (!cancelled) {
          setState(isInsideTelegram() ? { status: 'outside' } : { status: 'outside' });
        }
        return;
      }

      try {
        const result = await api.auth.telegram(initData);
        tokenStore.set(result.tokens);
        if (!cancelled) setState({ status: 'ready', user: result.user });
      } catch (error) {
        if (cancelled) return;
        const message =
          error instanceof RequestError
            ? error.message
            : 'The server is not reachable. Try again in a moment.';
        setState({ status: 'error', message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => state, [state]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
